import { Fragment } from 'react';
import type { ProcessTaskType } from '@common/enums';
import { ProcessActivityStatus, ProcessTaskStatus, StepStatus } from '@common/enums';
import { never } from '@common/utils/never';
import { NullableFormattedDatetime } from '@frontend/components/elements/NullableFormattedDatetime';
import type { Color, IGraphSizes } from './helpers';
import type { IProcessWorkflowStep, IServiceWorkflowStep } from './WorkflowGraph';
import { getColorCode, getCoordinates } from './helpers';

export type MouseOverActivity = {
    name: string;
    performedDatetime?: string | Date;
    status?: ProcessActivityStatus;
};

export type MouseOverTask = {
    performedDatetime?: string | Date;
    performerName?: string;
    status: ProcessTaskStatus;
    type: ProcessTaskType;
};

interface IMouseOverStepProps {
    activities?: MouseOverActivity[];
    borderColor: Color;
    borderWidth: number;
    fillColor: Color | null;
    graphSizes: IGraphSizes;
    processStep?: IProcessWorkflowStep;
    serviceStep: IServiceWorkflowStep;
    tasks?: MouseOverTask[];
}

export function MouseOverStep(props: IMouseOverStepProps) {
    const { activities, fillColor, borderColor, borderWidth, graphSizes, serviceStep, processStep, tasks } = props;
    const position = serviceStep.displayOptions;
    const coordinates = getCoordinates(position, graphSizes);

    return (
        <foreignObject
            // ESLint randomly fails when checking this property. It is unclear why.
            // eslint-disable-next-line react/no-unknown-property
            fill={getColorCode('black')}
            height={1}
            style={{ overflow: 'visible' }}
            width={1}
            x={coordinates.x + (position.y === graphSizes.largestY ? graphSizes.nodeWidth + graphSizes.margin / 4 : 0)}
            y={coordinates.y + (position.y === graphSizes.largestY ? 0 : graphSizes.nodeHeight + graphSizes.margin / 8)}
        >
            <div
                style={{
                    backgroundColor: `${fillColor ? getColorCode(fillColor) : undefined}`,
                    border: `${borderWidth}px solid ${getColorCode(borderColor)}`,
                    fontSize: `${graphSizes.nodeHeight / 5}px`,
                    inlineSize: 'min-content',
                    lineHeight: `${graphSizes.nodeHeight / 4}px`,
                    padding: `${graphSizes.margin / 8}px`,
                    whiteSpace: 'nowrap',
                }}
            >
                BlockName: {serviceStep.serviceWorkflowBlock.name}
                <br />
                BlockType: {serviceStep.serviceWorkflowBlock.type}
                <br />
                Coordinates: {serviceStep.displayOptions.x}, {serviceStep.displayOptions.y}
                <br />
                {processStep && (
                    <>
                        Status: {processStep.status}
                        <br />
                    </>
                )}
                {processStep && showProcessStepPerformedDatetime(processStep.status) && (
                    <>
                        Uitgevoerd: <NullableFormattedDatetime value={processStep.performedDatetime} />
                        <br />
                    </>
                )}
                {activities && activities.length > 0 && (
                    <>
                        Activities:
                        <br />
                        {activities.map((activity) => (
                            <Fragment key={activity.name}>
                                {activity.name}
                                <br />
                                {activity.status && (
                                    <>
                                        Status: {activity.status}
                                        <br />
                                    </>
                                )}
                                {showActivityPerformedDatetime(activity.status) && (
                                    <>
                                        Uitgevoerd: <NullableFormattedDatetime value={activity.performedDatetime} />
                                        <br />
                                    </>
                                )}
                            </Fragment>
                        ))}
                    </>
                )}
                {tasks && tasks.length > 0 && (
                    <>
                        Tasks:
                        <br />
                        {tasks.map((task) => (
                            <Fragment key={task.type}>
                                Type: {task.type}
                                <br />
                                Performer: {task.performerName}
                                <br />
                                {task.status && (
                                    <>
                                        Status: {task.status}
                                        <br />
                                    </>
                                )}
                                {showTaskPerformedDatetime(task.status) && (
                                    <>
                                        Uitgevoerd: <NullableFormattedDatetime value={task.performedDatetime} />
                                        <br />
                                    </>
                                )}
                            </Fragment>
                        ))}
                    </>
                )}
            </div>
        </foreignObject>
    );
}

function showProcessStepPerformedDatetime(status: StepStatus) {
    switch (status) {
        case StepStatus.failed:
        case StepStatus.finished:
        case StepStatus.inProgress:
        case StepStatus.obsolete:
            return true;
        case StepStatus.available:
        case StepStatus.cancelled:
        case StepStatus.scheduled:
            return false;
        default:
            never(status);
    }
}

function showActivityPerformedDatetime(status?: ProcessActivityStatus) {
    switch (status) {
        case ProcessActivityStatus.complete:
        case ProcessActivityStatus.draft:
            return true;
        case ProcessActivityStatus.cancelled:
            return false;
    }
}

function showTaskPerformedDatetime(status?: ProcessTaskStatus) {
    switch (status) {
        case ProcessTaskStatus.inProgress:
        case ProcessTaskStatus.closed:
            return true;
        case ProcessTaskStatus.open:
        case ProcessTaskStatus.cancelled:
            return false;
    }
}
